import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { TrackCard } from '../components/TrackCard';
import { Button } from '../components/ui/button';
import { 
  Store, 
  Users, 
  Globe, 
  Shield, 
  ArrowRight,
  ChevronRight,
  Music2,
  Zap
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const HomePage = () => {
  const [featuredTracks, setFeaturedTracks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeaturedTracks = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/tracks/featured`);
        setFeaturedTracks(response.data);
      } catch (error) {
        console.error('Error fetching featured tracks:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedTracks();
  }, []);

  const stats = [
    { value: '1,200+', label: 'Artists' },
    { value: '10K+', label: 'Tracks' },
    { value: '50K+', label: 'Sales' },
    { value: '50+', label: 'Countries' }
  ];

  const features = [
    {
      icon: Music2,
      title: 'License Music NFTs',
      description: 'Get exclusive licenses for Sync, Commercial, or Full NFT ownership of premium music.'
    },
    {
      icon: Users,
      title: 'Support Artists Directly',
      description: 'Your purchases go directly to the creators. No middlemen, just pure artist support.'
    },
    {
      icon: Globe,
      title: 'Global Distribution',
      description: 'Access music from artists across 50+ countries, all in one marketplace.'
    },
    {
      icon: Shield,
      title: 'Verified Ownership',
      description: 'Every purchase is tracked and verified. Your licenses are secure and transferable.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-dark">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-900 to-emerald-950/30" />
        <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-emerald-500/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-cyan-500/5 rounded-full blur-[100px]" />

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full mb-6 animate-fade-in-up">
            <Zap className="w-4 h-4 text-emerald-400" />
            <span className="text-xs font-semibold uppercase tracking-wider text-emerald-400">
              Music NFT Marketplace
            </span>
          </div>
          
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter text-white mb-6 animate-fade-in-up animation-delay-100">
            Welcome to
            <span className="block font-bold text-gradient">Ziki Store</span>
          </h1>

          <p className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto mb-10 animate-fade-in-up animation-delay-200">
            Buy, sell, and license exclusive music NFTs from world-class artists. 
            Own a piece of musical heritage.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up animation-delay-300">
            <Button
              asChild
              className="bg-emerald-500 text-slate-900 hover:bg-emerald-400 font-semibold px-8 py-6 text-lg rounded-lg"
              data-testid="hero-explore-btn"
            >
              <Link to="/marketplace">
                Explore Marketplace
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="border-slate-700 text-white hover:bg-slate-800 px-8 py-6 text-lg rounded-lg"
              data-testid="hero-artist-btn"
            >
              <Link to="/register">
                Become an Artist
                <ChevronRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-20 pt-10 border-t border-white/10 animate-fade-in-up animation-delay-400">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-3xl md:text-4xl font-semibold text-white">{stat.value}</p>
                <p className="text-sm text-slate-500 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
          <span className="text-xs text-slate-600 uppercase tracking-wider">Scroll</span>
          <div className="w-px h-8 bg-gradient-to-b from-slate-600 to-transparent" />
        </div>
      </section>

      {/* Featured Tracks Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-3 block">
                Featured
              </span>
              <h2 className="text-3xl sm:text-4xl font-medium tracking-tight text-white">
                Trending Tracks
              </h2>
            </div>
            <Link
              to="/marketplace"
              className="hidden md:flex items-center gap-2 text-slate-400 hover:text-emerald-400 transition-colors"
              data-testid="view-all-tracks"
            >
              View All
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-slate-800/50 animate-pulse aspect-[3/4] rounded-lg" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="featured-tracks-grid">
              {featuredTracks.map((track) => (
                <TrackCard key={track.id} track={track} />
              ))}
            </div>
          )}

          <div className="mt-10 text-center md:hidden">
            <Button
              asChild
              variant="outline"
              className="border-slate-700 text-white hover:bg-slate-800 rounded-lg"
            >
              <Link to="/marketplace">
                View All Tracks
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 md:py-32 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-3 block">
              Why Ziki Store
            </span>
            <h2 className="text-3xl sm:text-4xl font-medium tracking-tight text-white">
              The Future of Music Ownership
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-gradient-card border border-white/5 p-6 rounded-lg hover:border-emerald-500/30 transition-colors group"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 bg-emerald-500/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-emerald-500/20 transition-colors">
                  <feature.icon className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/20 p-12 md:p-16 rounded-2xl">
            <h2 className="text-3xl sm:text-4xl font-medium tracking-tight text-white mb-4">
              Ready to Start Your Collection?
            </h2>
            <p className="text-lg text-slate-400 mb-8 max-w-xl mx-auto">
              Join thousands of collectors and artists building the future of music.
            </p>
            <Button
              asChild
              className="bg-emerald-500 text-slate-900 hover:bg-emerald-400 font-semibold px-8 py-6 text-lg rounded-lg"
              data-testid="cta-get-started"
            >
              <Link to="/register">
                Get Started Today
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-950 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
                <Store className="w-4 h-4 text-slate-900" />
              </div>
              <span className="font-bold text-white">Z-Store</span>
            </div>
            <p className="text-sm text-slate-500">
              © 2026 Ziki Store. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/marketplace" className="text-sm text-slate-400 hover:text-white transition-colors">
                Marketplace
              </Link>
              <Link to="/artists" className="text-sm text-slate-400 hover:text-white transition-colors">
                Artists
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
